import { useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ArrowDown, ArrowUp } from "../../icons";
import "./RecordTable.scss";
import { RecordTableColumn, RecordTableProps } from "./RecordTable.types";
import { useColumnResize } from "../../hooks/useColumnResize";

const DEFAULT_WIDTH = 150;
const DEFAULT_MIN_WIDTH = 60;
// Matches the 2.75rem in RecordTable.scss, so the inline width and the rule agree.
const SELECT_WIDTH = 44;

interface SortState {
  key: string;
  dir: "asc" | "desc";
}

export function compareRowValues(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
  key: string,
  dir: "asc" | "desc"
): number {
  // An option set or lookup sent as { Id, Name } sorts by its name.
  const text = (value: unknown) =>
    typeof value === "object" && value !== null
      ? String((value as { Name?: unknown }).Name ?? "")
      : String(value ?? "");
  const comparison = text(a[key]).localeCompare(text(b[key]), undefined, {
    numeric: true,
  });

  return dir === "asc" ? comparison : -comparison;
}

/**
 * Compares by each sort in turn, so a later one breaks the ties the one before it
 * leaves. Entries with no key, and repeats of a key already applied, are skipped.
 */
export function compareRowsBy(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
  sorts: ({ key: string; dir: "asc" | "desc" } | null | undefined)[]
): number {
  const applied = new Set<string>();

  for (const sort of sorts) {
    if (!sort?.key || applied.has(sort.key)) {
      continue;
    }

    applied.add(sort.key);
    const comparison = compareRowValues(a, b, sort.key, sort.dir);

    if (comparison !== 0) {
      return comparison;
    }
  }

  return 0;
}

export function RecordTable<TRow extends Record<string, unknown>>({
  columns,
  rows,
  selection,
  emptyMessage = "No records found.",
  className = "",
  defaultSortKey,
  defaultSortDir = "asc",
  onSortChange,
  sortKey,
  sortDir = "asc",
  stickyHeader = false,
}: RecordTableProps<TRow>) {
  const [initialWidths] = useState<Record<string, number>>(() =>
    Object.fromEntries(
      columns.map((column) => [column.key, column.width ?? DEFAULT_WIDTH])
    )
  );
  const {
    getHandleProps,
    wasDragged,
    widths,
  } = useColumnResize(initialWidths);
  const [internalSort, setInternalSort] = useState<SortState | null>(
    defaultSortKey ? { key: defaultSortKey, dir: defaultSortDir } : null
  );

  const sortControlledByParent = onSortChange !== undefined;
  const sort = useMemo(
    () =>
      sortControlledByParent
        ? sortKey
          ? { key: sortKey, dir: sortDir }
          : null
        : internalSort,
    [sortControlledByParent, sortKey, sortDir, internalSort]
  );

  const handleHeaderClick = useCallback(
    (column: RecordTableColumn<TRow>) => {
      if (!column.sortable || wasDragged()) {
        return;
      }

      const nextDir: "asc" | "desc" =
        sort?.key === column.key && sort.dir === "asc" ? "desc" : "asc";

      if (onSortChange) {
        onSortChange(column.key, nextDir);
        return;
      }

      setInternalSort({ key: column.key, dir: nextDir });
    },
    [onSortChange, sort, wasDragged]
  );

  const sortedRows =
    sort && !sortControlledByParent
      ? [...rows].sort((a, b) => compareRowValues(a, b, sort.key, sort.dir))
      : rows;

  const totalWidth = columns.reduce(
    (sum, column) => sum + (widths[column.key] ?? column.width ?? DEFAULT_WIDTH),
    selection ? SELECT_WIDTH : 0
  );

  const selectedIds = selection ? new Set(selection.selectedIds) : null;
  const isSelected = (row: TRow) =>
    selectedIds?.has(selection?.rowId(row) ?? "") ?? false;
  // "All" is the page, not the result set: the checkbox can only speak for the rows
  // it can see. An empty page is not all-selected.
  const allPagesSelected = selection?.allSelected ?? false;
  const selectedOnPage = sortedRows.filter(isSelected).length;
  const allSelected =
    allPagesSelected ||
    (sortedRows.length > 0 && selectedOnPage === sortedRows.length);
  const someSelected = !allSelected && selectedOnPage > 0;
  const maxSelected = selection?.maxSelected;
  // With no checkbox column, the first column sits against the table's edge.
  const firstCellClass = selection ? "" : "ps-3";
  const selectionFull =
    maxSelected !== undefined && (selectedIds?.size ?? 0) >= maxSelected;
  // Ticking the rest of the page must not take the selection past the limit.
  const pageCheckboxDisabled =
    allPagesSelected ||
    sortedRows.length === 0 ||
    (!allSelected &&
      maxSelected !== undefined &&
      (selectedIds?.size ?? 0) + (sortedRows.length - selectedOnPage) >
        maxSelected);

  return (
    <table
      className={classNames(
        "table page-record-table",
        { "page-record-table--sticky-header": stickyHeader },
        className
      )}
      style={{ minWidth: totalWidth }}
    >
      <thead>
        <tr>
          {selection && (
            <th
              className="page-record-table__select"
              style={{ width: SELECT_WIDTH }}
            >
              <input
                className="form-check-input"
                type="checkbox"
                aria-label="Select all records on this page"
                checked={allSelected}
                disabled={pageCheckboxDisabled}
                ref={(element) => {
                  if (element) {
                    element.indeterminate = someSelected;
                  }
                }}
                onChange={() =>
                  selection.onToggleAll(sortedRows, !allSelected)
                }
              />
            </th>
          )}
          {columns.map((column, columnIndex) => {
            const minWidth = column.minWidth ?? DEFAULT_MIN_WIDTH;
            const resizable = column.resizable !== false;
            const columnSortDir = sort?.key === column.key ? sort.dir : null;
            const isLastColumn = columnIndex === columns.length - 1;

            return (
              <th
                key={column.key}
                style={
                  isLastColumn
                    ? { minWidth: widths[column.key] }
                    : { width: widths[column.key] }
                }
                className={classNames(
                  { "page-record-table__th--sortable": column.sortable },
                  columnIndex === 0 && firstCellClass
                )}
                onClick={() => handleHeaderClick(column)}
              >
                {column.label}
                {column.sortable && columnSortDir && (
                  <FontAwesomeIcon
                    className="page-record-table__sort-indicator"
                    icon={columnSortDir === "asc" ? ArrowUp : ArrowDown}
                    size="sm"
                  />
                )}
                {resizable && (
                  <div
                    className="page-record-table__resize-handle"
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={`Resize ${column.label} column`}
                    {...getHandleProps(column.key, {
                      minWidth,
                      startWidth: column.width ?? DEFAULT_WIDTH,
                    })}
                  />
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {sortedRows.length === 0 ? (
          <tr>
            <td
              colSpan={columns.length + (selection ? 1 : 0)}
              className="text-muted text-center py-3"
            >
              {emptyMessage}
            </td>
          </tr>
        ) : (
          sortedRows.map((row, rowIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={rowIndex}>
              {selection && (
                <td className="page-record-table__select">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    aria-label={`Select ${
                      selection.rowLabel?.(row) ?? "record"
                    }`}
                    checked={allPagesSelected || isSelected(row)}
                    disabled={
                      allPagesSelected || (!isSelected(row) && selectionFull)
                    }
                    onChange={() => selection.onToggleRow(row)}
                  />
                </td>
              )}
              {columns.map((column, columnIndex) => (
                <td
                  key={column.key}
                  className={columnIndex === 0 ? firstCellClass : undefined}
                >
                  {column.accessor
                    ? column.accessor(row)
                    : String(
                        (row as Record<string, unknown>)[column.key] ?? ""
                      )}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}
